/**
 * Die drei Ansichten, die **ohne Anmeldung** erreichbar sind (HM-026):
 * Passwort vergessen, neues Passwort setzen, Adresse bestätigen.
 *
 * Sie benutzen seither denselben Rahmen wie Anmelden und Registrieren —
 * `components/auth-layout.tsx`, aus `templates/auth-web/AuthWeb.dc.html`: links
 * die grüne Markenfläche, rechts das Formular. **Das war seit HM-059 die
 * Absicht und ist erst jetzt der Zustand:** Bis hierher stand hier ein eigener
 * `Rahmen` — zentrierte Wortmarke über einer schmalen Karte —, also genau die
 * zweite, gleich aussehende Fassung, gegen die HM-059 geschrieben ist. Wer die
 * Anmeldung änderte, ließ diese drei Ansichten stehen; wer aus einer E-Mail
 * kam, landete auf einer Seite, die nach einer anderen Anwendung aussah.
 *
 * **Die Markenfläche sagt, wozu die Ansicht führt.** Für das vergessene
 * Passwort steht dort, was der Entwurf vorgibt: Die Messung läuft weiter, ein
 * vergessenes Passwort kostet keine Daten. Das ist die Frage, die sich an
 * dieser Stelle wirklich stellt — und die einzige, deren Antwort dieses Produkt
 * ohne Einschränkung geben kann.
 *
 * **Die Anrede ist das informelle „du“**, klein geschrieben, wie im ganzen Produkt.
 */
import { Alert, Button, Input } from "@hausmeter/ui";
import { useEffect, useState, type FormEvent } from "react";
import { AuthBackLink, AuthHeading, AuthLayout, AuthNote } from "../components/auth-layout.js";
import { PasswordField } from "../components/password-field.js";
import {
  fieldErrorsFromDetails,
  PASSWORD_MIN_LENGTH,
  passwordError,
} from "../lib/registration-fields.js";
import { navigate } from "../lib/router.js";
import { RequestError, requestPasswordReset, resetPassword, verifyEmail } from "../lib/session.js";

/**
 * **Die Stunde steht als Satz und nicht als Konstante mit Test daneben.** Die
 * Frist lebt in `apps/api/src/auth/account-recovery.service.ts`
 * (`LIFETIME_SECONDS.password_reset`) — in einer **App**, und Apps importieren
 * sich nicht gegenseitig; auch ein Test käme nicht heran. Anders als bei den
 * Fristen der Rechtstexte (`lib/legal.ts`) gibt es hier also keine Quelle, gegen
 * die sich prüfen ließe. Wer sie im Dienst ändert, ändert diese beiden Sätze
 * mit — und die Zeile in der E-Mail, die dieselbe Stunde nennt.
 */

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      headline="Die Messung läuft weiter."
      lead="Dein Gerät misst und speichert unabhängig vom Konto. Ein vergessenes Passwort kostet keine Daten."
    >
      <AuthBackLink to={{ kind: "start" }}>Zurück zur Anmeldung</AuthBackLink>

      <AuthHeading
        title="Passwort zurücksetzen"
        lead="Gib die E-Mail-Adresse deines Kontos ein. Der Link im Postfach gilt eine Stunde und lässt sich einmal verwenden."
      />

      {/*
        **Das Formular bleibt nach dem Absenden stehen.** Vorher trat an seine
        Stelle eine eigene „fertig"-Ansicht; von dort führte der Weg zu einem
        Tippfehler in der Adresse nur über die Zurück-Taste. Wer die Nachricht
        nicht bekommt, will genau zwei Dinge tun können — die Adresse
        berichtigen und es noch einmal versuchen —, und beides steht damit da,
        wo er hinsieht. So legt es auch der Entwurf an: Satz unter dem Knopf,
        Formular darüber.
      */}
      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}
        onSubmit={(event) => void submit(event)}
      >
        <Input
          label="E-Mail"
          type="email"
          size="lg"
          value={email}
          autoComplete="email"
          required
          onChange={(event) => {
            setEmail(event.target.value);
            /*
              Der Satz gilt für die Adresse, die abgeschickt wurde. Tippt jemand
              weiter, gilt er nicht mehr — er stehen zu lassen hieße zu
              behaupten, an die neue Adresse sei etwas unterwegs.
            */
            setSent(false);
          }}
        />

        {error !== null && <Alert>{error}</Alert>}

        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? "Einen Moment …" : sent ? "Erneut senden" : "Link senden"}
        </Button>
      </form>

      {/*
        **„Wenn es ein Konto gibt"** — und nicht „Wir haben dir eine E-Mail
        geschickt". Der Server sagt bewusst nicht, ob die Adresse bekannt ist;
        eine Oberfläche, die es doch behauptet, hebt die Zusage auf. Der Satz ist
        etwas umständlicher und dafür wahr.
      */}
      {sent && (
        <AuthNote>
          Wenn es zu dieser Adresse ein Konto gibt, ist die Nachricht unterwegs. Sieh auch im
          Spam-Ordner nach.
        </AuthNote>
      )}
    </AuthLayout>
  );
}

const PASSWORD_FIELD_ID = "reset-password-new";

export function ResetPasswordPage({ token }: { readonly token: string | null }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Wie bei der Registrierung: Die Regel steht nach dem ersten Absenden rot am
     Feld (`lib/registration-fields.ts`), nicht als Blase des Browsers. */
  const [attempted, setAttempted] = useState(false);
  const [rejected, setRejected] = useState<string | undefined>(undefined);
  const fieldError = (attempted ? passwordError(password) : undefined) ?? rejected;

  if (token === null) {
    return (
      <AuthLayout
        headline="Die Messung läuft weiter."
        lead="Dein Gerät misst und speichert unabhängig vom Konto. Ein vergessenes Passwort kostet keine Daten."
      >
        <AuthHeading
          title="Dieser Link ist unvollständig"
          lead="Öffne den Link aus der E-Mail vollständig, oder fordere einen neuen an."
        />
        <Button size="lg" block onClick={() => navigate({ kind: "forgot-password" })}>
          Neuen Link anfordern
        </Button>
      </AuthLayout>
    );
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (token === null) return;
    setAttempted(true);
    if (passwordError(password) !== undefined) {
      setError(null);
      document.getElementById(PASSWORD_FIELD_ID)?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      /**
       * **Kein „fertig"-Bildschirm.** Der Aufruf hat eine Sitzung begonnen; die
       * App zeigt daraufhin von selbst die Anwendung. Ein Zwischenschritt mit
       * „Weiter" wäre ein Klick, der nichts entscheidet.
       *
       * Die Adresse wird dabei ersetzt und nicht angehängt: Der verbrauchte
       * Token soll nicht im Verlauf stehen bleiben, und die Zurück-Taste soll
       * nicht auf ein Formular führen, das nicht mehr funktioniert.
       */
      navigate({ kind: "start" }, { replace: true });
    } catch (cause: unknown) {
      const onField =
        cause instanceof RequestError
          ? fieldErrorsFromDetails(cause.details, { password }).password
          : undefined;
      if (onField !== undefined) {
        setRejected(onField);
        document.getElementById(PASSWORD_FIELD_ID)?.focus();
        return;
      }
      setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      headline="Nur mit dem Link aus deinem Postfach."
      lead="Er gilt eine Stunde und lässt sich einmal verwenden. Danach bist du sofort angemeldet."
    >
      <AuthHeading
        title="Neues Passwort setzen"
        lead="Wähle ein Passwort, das du noch nirgends sonst benutzt."
      />

      <form
        style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        <PasswordField
          id={PASSWORD_FIELD_ID}
          label="Neues Passwort"
          size="lg"
          value={password}
          autoComplete="new-password"
          // Dieselbe Zusage wie im Vertrag (PasswordSchema).
          hint={`Mindestens ${PASSWORD_MIN_LENGTH} Zeichen.`}
          error={fieldError}
          required
          onChange={(event) => {
            setPassword(event.target.value);
            setRejected(undefined);
          }}
        />

        {error !== null && <Alert>{error}</Alert>}

        {/*
          Der Hinweis steht **vor** dem Absenden und nicht danach: Dass alle
          anderen Sitzungen enden, ist eine Folge, die man vorher wissen will.
        */}
        <p style={{ margin: 0, color: "var(--text-faint)", fontSize: 12 }}>
          Mit dem neuen Passwort werden alle bestehenden Sitzungen beendet — auch auf anderen
          Geräten.
        </p>

        <Button type="submit" size="lg" block disabled={busy}>
          {busy ? "Einen Moment …" : "Passwort setzen"}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage({ token }: { readonly token: string | null }) {
  const [state, setState] = useState<"laeuft" | "fertig" | "fehler">(
    token === null ? "fehler" : "laeuft",
  );
  const [error, setError] = useState<string | null>(
    token === null ? "Dieser Link ist unvollständig." : null,
  );

  useEffect(() => {
    if (token === null) return;
    let gilt = true;

    /**
     * **Bestätigt beim Öffnen, ohne Knopf.** Wer den Link anklickt, hat die
     * Handlung bereits vollzogen; ein „Jetzt bestätigen"-Knopf danach wäre eine
     * zweite Zustimmung zu derselben Sache.
     */
    verifyEmail(token)
      .then(() => {
        if (gilt) setState("fertig");
      })
      .catch((cause: unknown) => {
        if (!gilt) return;
        setError(cause instanceof Error ? cause.message : "Unbekannter Fehler.");
        setState("fehler");
      });

    return () => {
      gilt = false;
    };
  }, [token]);

  return (
    /*
      **Ein Rahmen für alle drei Zustände.** Er wechselt nicht, während bestätigt
      wird — sonst sprängen Markenfläche und Karte in dem Augenblick, in dem die
      Antwort eintrifft, und das sähe nach einem Fehler aus.
    */
    <AuthLayout
      headline="Deine Adresse gehört zu deinem Konto."
      lead="Die Bestätigung ist der Weg zurück, falls du dein Passwort einmal vergisst. Am Konto ändert sie sonst nichts — es ist auch ohne sie vollständig nutzbar."
    >
      {state === "laeuft" && (
        <AuthHeading title="Einen Moment …" lead="Deine E-Mail-Adresse wird gerade bestätigt." />
      )}

      {state === "fehler" && (
        <>
          <AuthHeading
            title="Nicht bestätigt"
            lead="Dein Konto ist davon unberührt: Du kannst dich anmelden und die Bestätigung im Profil erneut anfordern."
          />
          <Alert>{error ?? "Unbekannter Fehler."}</Alert>
          <Button size="lg" block onClick={() => navigate({ kind: "start" })}>
            Zur Anmeldung
          </Button>
        </>
      )}

      {state === "fertig" && (
        <>
          <AuthHeading
            title="Adresse bestätigt"
            lead="Damit steht dir der Weg über ein vergessenes Passwort offen."
          />
          {/*
            **Keine Sitzung.** Zugriff auf ein Postfach ist kein Nachweis, dass
            hier der Kontoinhaber sitzt — der Knopf führt zur Anmeldung und
            nicht in die Anwendung.
          */}
          <Button size="lg" block onClick={() => navigate({ kind: "start" })}>
            Zur Anmeldung
          </Button>
        </>
      )}
    </AuthLayout>
  );
}
